// ブロック入札タブ (BlockBidTable) のステータス列が表示する「11 区分」の導出。
//
// AC-BSP-049 はブロック入札が all-or-nothing (= 部分約定が発生しない) 方式のため、
// 通常入札の 12 区分 (AC-BSP-047) から「部分約定」を除いた 11 区分を要求する:
//   予約中 / 下書き / 承認待ち / 差戻し / 送信中 / 入札中 / 約定待ち /
//   全約定 / 未約定 / 取消 / 失効
// AC-BSP-048 の「未約定」と「失効」を別バッジにする要求もそのまま適用される。
//
// 判定規則の実体は通常入札と共通の `bidStatusCategory.ts` に置き、 ここは
// 約定軸 (BiddingBlockBidStatus) の写像だけを持つ。
import { BiddingBlockBidStatus } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import type {
  BiddingApprovalState,
  BiddingBidSendStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import {
  type BidMatchOutcome,
  type BidStatusCategoryWithoutPartial,
  deriveBidStatusCategory,
} from './bidStatusCategory'
import { reviewStartedBeforeDeadline } from './needsReview'

/** AC-BSP-049 の 11 区分 (= AC-BSP-047 の 12 区分から部分約定を除く)。 */
export type BlockBidStatusCategory = BidStatusCategoryWithoutPartial

/** 判定に要る field だけを受ける (= 行全体を組み立てずに単体テストできる)。 */
export type BlockBidStatusCategoryInput = {
  needsReview?: boolean
  needsReviewSince?: Date
  targetDate?: Date
  approvalState: BiddingApprovalState
  bidStatus: BiddingBlockBidStatus
  sendStatus?: BiddingBidSendStatus
  /**
   * JEPX が受け付けていたかを確かめているグループか (backend が応答時点で決める
   * `receipt_checking`、 AC-BSP-117)。 真の間は締切の後も 「送信中」 のまま出す。
   * グループ単位の値で、 グループの全 leg 行に同じ値を展開する。
   */
  receiptChecking?: boolean
}

// 取下処理中・取下失敗は板に残っている札なので約定待ちと同じ扱い (AC-BSP-057b)。
// 失効 (EXPIRED、 AC-BSP-099 / AC-BSP-011 / AC-BSP-117) は通常入札の EXPIRED と同じく FAILED と同じ 'failed' へ写し、
// 承認軸・送信軸に関わらず「失効」 に確定する。
const LEG_OUTCOME_BY_BID_STATUS: Record<
  BiddingBlockBidStatus,
  BidMatchOutcome | 'cancelled'
> = {
  [BiddingBlockBidStatus.UNSPECIFIED]: 'pending',
  [BiddingBlockBidStatus.PENDING]: 'pending',
  [BiddingBlockBidStatus.MATCHED]: 'matched',
  [BiddingBlockBidStatus.UNMATCHED]: 'unmatched',
  [BiddingBlockBidStatus.FAILED]: 'failed',
  // 失効 (EXPIRED) は eSLJ の FAILED と同じ「失効」 (AC-BSP-011 / AC-BSP-117)。
  [BiddingBlockBidStatus.EXPIRED]: 'failed',
  [BiddingBlockBidStatus.CANCELLED]: 'cancelled',
  [BiddingBlockBidStatus.WITHDRAWING]: 'pending',
  [BiddingBlockBidStatus.WITHDRAW_FAILED]: 'pending',
}

/**
 * 承認状態バナーの見出し ([[resolveApprovalBannerHeading]]) へ渡す約定結果。 バナーは取消の
 * leg を区別しないので、 表の 'cancelled' は約定結果が未確定の 'pending' に写す。
 */
export const toMatchOutcome = (
  status: BiddingBlockBidStatus,
): BidMatchOutcome => {
  const outcome = LEG_OUTCOME_BY_BID_STATUS[status]
  return outcome === 'cancelled' ? 'pending' : outcome
}

/**
 * 承認状態 + 送信状態 + 約定結果の 3 軸から、 ステータス列の 11 区分
 * ([[BlockBidStatusCategory]]) を 1 つ返す純関数。 規則は
 * [[deriveBidStatusCategory]] を参照。
 */
export const deriveBlockBidStatusCategory = (
  row: BlockBidStatusCategoryInput,
  isPastDeadline: boolean,
): BlockBidStatusCategory => {
  const outcome = LEG_OUTCOME_BY_BID_STATUS[row.bidStatus]
  if (outcome === 'cancelled') {
    return 'cancelled'
  }
  return deriveBidStatusCategory(
    {
      approvalState: row.approvalState,
      sendStatus: row.sendStatus,
      matchOutcome: outcome,
      hasWorkflow: false,
      receiptChecking: row.receiptChecking,
    },
    isPastDeadline && !reviewStartedBeforeDeadline(row),
  )
}
